import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { CONNECTIONS_CHANGED_EVENT, getConsoleConnections } from "../lib/api";
import { sha256Fallback } from "../lib/sha256";

function subscribe(notify: () => void) {
  window.addEventListener(CONNECTIONS_CHANGED_EVENT, notify);
  return () => window.removeEventListener(CONNECTIONS_CHANGED_EVENT, notify);
}
function snapshot() {
  // No raw credentials in React keys or diagnostics. All routes (including
  // legacy Jobs/Traces) must leave old cached records and forms behind.
  return sha256Fallback(new TextEncoder().encode(JSON.stringify(getConsoleConnections())));
}
export function ConnectionQueryProvider({ children }: { children: ReactNode }) {
  const scope = useSyncExternalStore(subscribe, snapshot, snapshot);
  return <ScopedQueries key={scope}>{children}</ScopedQueries>;
}
function ScopedQueries({ children }: { children: ReactNode }) {
  const [client] = useState(() => new QueryClient({ defaultOptions: { queries: { staleTime: 5_000, retry: 1 } } }));
  const lifetime = useRef(0);
  useEffect(() => {
    const generation = ++lifetime.current;
    return () => {
      // StrictMode replays effects without replacing the client/observers. Do
      // not destroy their cache during that replay; real unmount still retires
      // the old account's client; it cannot publish to its replacement.
      queueMicrotask(() => {
        if (generation === lifetime.current) { void client.cancelQueries(); client.clear(); }
      });
    };
  }, [client]);
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
